/**
 * Browser MCP tool registration — daemon adapter.
 *
 * Thin layer that maps MCP tool calls to the session daemon,
 * ensuring the CLI gets the SAME semantic diff, snapshot formatting,
 * click validation, and recovery notices as the cloud agent.
 *
 * The daemon owns all browser sessions. MCP is just a protocol adapter.
 *
 * @module
 */

import { getBrowserToolDefinitionsWithLifecycle } from '@chatsdet/browser-core';
import {
  createSession,
  deleteSession,
  deleteAllSessions,
  callTool,
  resolveTargetSession,
} from '../session/daemon-client.js';
import { formatMCPContent, toolJson, toolText } from './tool-helpers';
import { USER_PAUSED_ERROR } from '../session/types.js';
import type { ToolResult } from '@chatsdet/browser-core';

/** Track the active session ID within this MCP server instance */
let activeSessionId: string | null = null;

/**
 * Get tool definitions for browser MCP tools (including lifecycle).
 */
export function getBrowserMCPToolDefinitions() {
  return getBrowserToolDefinitionsWithLifecycle();
}

/**
 * Handle a browser tool call.
 * Returns null if the tool name is not a browser tool.
 */
export async function handleBrowserTool(name: string, args: Record<string, unknown>) {
  // Lifecycle tools
  if (name === 'browser_start') {
    try {
      const result = await createSession({
        headless: (args.headless as boolean) ?? false,
        storageStatePath: args.storageStatePath as string | undefined,
      });

      if (!result.ok) {
        return toolJson({ ok: false, error: result.error });
      }

      activeSessionId = result.data!.id;
      return toolJson({
        ok: true,
        sessionId: activeSessionId,
        note: 'Browser started. You can now use browser_navigate, browser_click, and other browser tools.',
      });
    } catch (err) {
      return toolJson({ ok: false, error: err instanceof Error ? err.message : String(err) });
    }
  }

  if (name === 'browser_stop') {
    try {
      if (activeSessionId) {
        await deleteSession(activeSessionId);
        activeSessionId = null;
      } else {
        // Stop all sessions if no specific one tracked
        await deleteAllSessions();
      }
      return toolJson({ ok: true, note: 'Browser stopped and cleaned up.' });
    } catch (err) {
      return toolJson({ ok: false, error: err instanceof Error ? err.message : String(err) });
    }
  }

  // All other browser_* tools require an active session
  if (!name.startsWith('browser_')) return null;

  try {
    // Resolve target session: prefer tracked MCP session, then auto-resolve
    let sessionId = activeSessionId;
    if (!sessionId) {
      try {
        const target = await resolveTargetSession();
        sessionId = target.id;
        activeSessionId = sessionId;
      } catch {
        return toolJson({
          ok: false,
          error: 'No active browser session. Start one with browser_start first.',
        });
      }
    }

    // Strip 'browser_' prefix for the daemon tool name
    const toolName = name.replace(/^browser_/, '');
    const result = await callTool(sessionId, toolName, args);

    if (!result.ok) {
      // If session not found, clear tracking and report error
      if (result.error?.includes('not found')) {
        activeSessionId = null;
      }
      // The user is actively driving the browser — the daemon deferred this
      // action and will auto-resume once they go idle. Surface as informational
      // text (not an error) so Claude waits rather than retrying in a loop.
      if (result.errorCode === USER_PAUSED_ERROR) {
        return toolText(
          result.error ?? 'The user is interacting with the browser; deferring until they are idle.'
        );
      }
      return toolJson({ ok: false, error: result.error, tool: name });
    }

    // Convert daemon response back to ToolResult format for MCP formatting
    const toolResult: ToolResult = result.images?.length
      ? { text: result.text ?? '', images: result.images }
      : (result.text ?? '');

    return formatMCPContent(toolResult);
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    return toolJson({ ok: false, error: message, tool: name });
  }
}
